/**
 * Currently, multiple types in this file are just copied from BE type declarations
 * for getBehaviorConfig.
 *
 * TODO: Reuse BE typings, when they're available through @provision/entity-types package.
 */

export interface BehaviorConfig {
  version: string;
  triggers: ConfigTrigger[];
  conditionTypes: ConfigConditionType[];
  actionTypes: ConfigActionType<ActionType>[];
  operators: OperatorsByFormControlType;
  conditions: ConfigConditions;
  actions: ConfigActions;
}

export enum BehaviorTrigger {
  ConversationCreated = 'conversation_created',
  ConversationUpdated = 'conversation_updated',
  ScheduledConversationCheck = 'scheduled_conversation_check',
  ConversationCreatedOrUpdated = 'conversation_created_or_updated',
  SiteCreated = 'site_created',
  SiteUpdated = 'site_updated',
  ClientCreated = 'client_created',
  ClientUpdated = 'client_updated',
  SignalIncidentCreated = 'signal_incident_created',
}

export interface ConfigTrigger {
  label: string;
  value: BehaviorTrigger;
}

export enum ConditionType {
  Data = 'data',
  ContactCenterHours = 'contactCenterHours',
  PartnerBusinessHours = 'partnerBusinessHours',
  Actor = 'actor',
  PartnerSetting = 'partnerSetting',
}

export interface ConfigConditionType {
  label: string;
  value: ConditionType;
}

export enum ActionType {
  Data = 'data',
  SendSlackMessage = 'sendSlackMessage',
  SendConversationMessage = 'sendConversationMessage',
  Webhook = 'webhook',
  Signal = 'signal',
  ManageConversationLabels = 'manageConversationLabels',
  ManageSubAssignees = 'manageSubAssignees',
  CreateTwilioTask = 'createTwilioTask',
  RunTriage = 'runTriage',
}
export interface ConfigActionType<T> {
  label: string;
  value: T;
}

export enum DataConditionOperator {
  Equals = '=',
  DoesNotEqual = '!=',
  Exists = 'exists',
  DoesNotExist = 'does_not_exist',
  Empty = 'empty',
  NotEmpty = 'not_empty',
  Contains = 'contains',
  DoesNotContain = 'does_not_contain',
  StartsWith = 'starts_with',
  EndsWith = 'ends_with',
  LessThan = '<',
  GreaterThan = '>',
  LessOrEqThan = '<=',
  GreaterOrEqThan = '>=',
  Between = 'between',
  NotBetween = 'notBetween',
  In = 'in',
  NotIn = 'not_in',
  Before = 'before',
  After = 'after',
  OnOrBefore = 'on_or_before',
  OnOrAfter = 'on_or_after',
  True = 'true',
  False = 'false',
  IsOneOf = 'is_one_of',
  IsNotOneOf = 'is_not_one_of',
}

type Arity = number | 'unary' | 'binary' | 'ternary' | null;

/**
 * @example { value: '=', label: 'Equal', arity: 'binary' }
 */
export interface ConfigOperator {
  value: DataConditionOperator;
  label: string;
  arity: Arity;
  /** When set, the operator is offered only for these triggers. */
  triggers?: BehaviorTrigger[] | null;
}

// UIType on BE originally, but renamed to avoid confusion with actual UI-specific types.
export enum FormControlType {
  Text = 'text',
  Textarea = 'textarea',
  RichText = 'rich-text',
  Url = 'url',
  List = 'list',
  Number = 'number',
  Date = 'date',
  DateTime = 'date-time',
  Boolean = 'boolean',
  ToggleGroup = 'toggle-group',
}

export type OperatorsByFormControlType = Record<FormControlType, ConfigOperator[]>;

interface ConfigOption {
  value: string;
  label: string;
}

export interface ConfigConditions {
  data: ConfigDataCondition[];
  partnerBusinessHours: ConfigHoursCondition[];
  contactCenterHours: ConfigHoursCondition[];
  actor: ActorCondition[];
  partnerSetting: PartnerSettingCondition[];
}

interface EntityBase {
  id: string | number;
  triggers: BehaviorTrigger[];
}

export interface DataEntityBase extends EntityBase {
  /**
   * Allows to group data conditions by entity.
   */
  group: string;
  /**
   * @example 'associatedAddresses/city'
   * Absolute path to the attribute to be used by the action/condition.
   * For example, 'Conversation/associatedAddresses/city'.
   */
  attribute: string;
  /**
   * Friendly name for the attribute.
   * @example 'Address City'
   */
  attributeLabel: string;
  /**
   * What kind of UI element should be used to set the value.
   */
  uiType: FormControlType;
  /**
   * For list UI type. Predefined values that can be selected.
   */
  options?: ConfigOption[];
  /**
   * For list UI type. Information to fetch values from the backend. Either `options` or `listEntity` should be set, not both.
   */
  optionsQuery?: {
    /**
     * @example "getPlatformUser"
     */
    graphqlQuery: string;
  };
  operators?: ConfigOperator[];
}

export type ConfigDataCondition = DataEntityBase;
export type ConfigHoursCondition = DataEntityBase;
export type ActorCondition = DataEntityBase;
export type PartnerSettingCondition = DataEntityBase;

export interface ConfigActions {
  data: ConfigDataAction[];
  sendSlackMessage: ConfigSendSlackMessageAction;
  sendConversationMessage: ConfigSendConversationMessageAction;
  webhook: ConfigWebhookAction;
  manageConversationLabels: ConfigManageConversationLabelsAction;
  manageSubAssignees: ConfigManageSubAssigneesAction;
  signal: ConfigSignalAction;
  createTwilioTask: ConfigCreateTwilioTaskAction;
  runTriage: ConfigRunTriageAction;
}

export type ConfigDataAction = DataEntityBase;

export interface TriggerPlaceholder {
  trigger: BehaviorTrigger;
  placeholder: string;
}

export interface ConfigCustomActionAttribute {
  name: string;
  uiType: FormControlType;
  label: string;
  placeholder?: string;
  /**
   * Example shown instead of `placeholder` when the rule's trigger matches.
   * A Site rule must not be offered Conversation variables.
   */
  placeholderByTrigger?: TriggerPlaceholder[];
  multiple?: boolean;
  options?: ConfigOption[];
  optionsQuery?: {
    graphqlQuery: string;
  };
  dependsOn?: {
    attribute: string;
    value: string;
  }[];
}

export interface ConfigSendSlackMessageAction {
  triggers: BehaviorTrigger[];
  attributes: ConfigCustomActionAttribute[];
}

export interface ConfigSendConversationMessageAction {
  triggers: BehaviorTrigger[];
  attributes: ConfigCustomActionAttribute[];
}
export interface ConfigSignalAction {
  triggers: BehaviorTrigger[];
  attributes: ConfigCustomActionAttribute[];
}

export interface ConfigManageConversationLabelsAction {
  triggers: BehaviorTrigger[];
  attributes: [
    ConfigManageConversationLabelsActionOperation,
    ConfigManageConversationLabelsActionLabelIds,
  ];
}

export interface ConfigManageConversationLabelsActionOperation {
  name: 'operation';
  uiType: FormControlType.List;
  label: string;
  options: ConfigOption[];
}

export interface ConfigManageConversationLabelsActionLabelIds {
  name: 'labelIds';
  uiType: FormControlType.List;
  label: string;
  optionsQuery: {
    graphqlQuery: string;
  };
}

export interface ConfigManageSubAssigneesAction {
  triggers: BehaviorTrigger[];
  attributes: [
    ConfigManageSubAssigneesActionOperation,
    ConfigManageSubAssigneesActionPlatformUserIds,
  ];
}

export interface ConfigManageSubAssigneesActionOperation {
  name: 'operation';
  uiType: FormControlType.List;
  label: string;
  options: ConfigOption[];
}

export interface ConfigManageSubAssigneesActionPlatformUserIds {
  name: 'platformUserIds';
  uiType: FormControlType.List;
  label: string;
  optionsQuery: {
    graphqlQuery: string;
  };
}

export interface ConfigCreateTwilioTaskAction {
  triggers: BehaviorTrigger[];
  attributes: ConfigCustomActionAttribute[];
}

export interface ConfigRunTriageAction {
  triggers: BehaviorTrigger[];
  attributes: ConfigCustomActionAttribute[];
}

export interface ConfigWebhookAction {
  triggers: BehaviorTrigger[];
  attributes: [
    {
      name: 'url';
      uiType: FormControlType.Text;
      label: string;
    },
    {
      name: 'method';
      uiType: FormControlType.List;
      label: string;
      options: ConfigOption[];
    },
    {
      name: 'body';
      uiType: FormControlType.Textarea;
      label: string;
      placeholder: string;
      placeholderByTrigger?: TriggerPlaceholder[];
    },
    {
      name: 'headers';
      uiType: FormControlType.Textarea;
      label: string;
    },
  ];
}

export type ConfigActionField = ConfigDataAction | ConfigCustomActionAttribute;
